<template>
	<div class="card shadow-sm mb-3">
		<div class="card-header bg-white pane-header">
			<i class="bi bi-house me-2"></i>Property
		</div>
		<div class="card-body">
			<div v-if="loading" class="text-muted small">
				<span
					class="spinner-border spinner-border-sm me-2"
					role="status"
					aria-hidden="true"
				></span>
				Loading property…
			</div>

			<p v-else-if="error" class="text-danger small mb-0">
				{{ error }}
			</p>

			<p v-else-if="!parcel" class="text-muted small mb-0">
				No parcel record at this communication's location.
			</p>

			<template v-else>
				<div v-if="badges.length > 0" class="d-flex flex-wrap gap-1 mb-2">
					<span
						v-for="badge in badges"
						:key="badge.label"
						:class="['badge', badge.cls]"
						:title="badge.title"
					>
						{{ badge.label }}
					</span>
				</div>

				<dl class="row mb-0 small">
					<template v-for="row in rows" :key="row.label">
						<dt class="col-sm-5">{{ row.label }}</dt>
						<dd class="col-sm-7">{{ row.value }}</dd>
					</template>
				</dl>

				<div v-if="parcel.site_ids.length > 0" class="mt-2 small">
					<span class="text-muted me-1">Sites:</span>
					<template v-for="(siteID, index) in parcel.site_ids" :key="siteID">
						<span v-if="index > 0" class="text-muted">, </span>
						<RouterLink :to="routes.SiteDetail(siteID)">
							{{ siteLabel(siteID) }}
						</RouterLink>
					</template>
				</div>

				<div class="mt-3">
					<button
						class="btn btn-sm btn-outline-secondary"
						:disabled="!parcel.geometry_geojson"
						@click="zoomToProperty"
					>
						<i class="bi bi-bounding-box me-1"></i>Zoom to property
					</button>
				</div>
			</template>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, watch } from "vue";
import { useRoutes } from "@/route/use";
import { useStoreSite } from "@/store/site";
import type { LngLatBounds } from "@/map/Map.vue";
import { boundsFromGeoJSON } from "@/map/util";
import type { CommunicationProperty } from "@/type/api";

interface Emits {
	(e: "zoom", bounds: LngLatBounds): void;
}
interface Props {
	property: CommunicationProperty | null;
	loading: boolean;
	error?: string;
	reportAddress?: string | null;
}
interface Badge {
	label: string;
	title: string;
	cls: string;
}
interface Row {
	label: string;
	value: string;
}

const props = withDefaults(defineProps<Props>(), {
	error: "",
	reportAddress: null,
});
const emit = defineEmits<Emits>();
const routes = useRoutes();
const storeSite = useStoreSite();

const parcel = computed(() => props.property?.parcel ?? null);

// The district's corrected situs address wins over the county's raw parcel
// address; the county value is only a fallback for display.
const effectiveRaw = computed(
	(): string =>
		parcel.value?.situs?.address_raw || parcel.value?.address_raw || "",
);
const effectiveAddress = computed((): string => effectiveRaw.value || "—");

// Compare loosely: casing, punctuation (periods, commas, hashes) and run-on
// whitespace must not make "1000 MAIN ST" differ from "1000 Main St.".
function normalizeAddress(value: string | null | undefined): string {
	if (!value) return "";
	return value.toUpperCase().replace(/[.,#]/g, " ").replace(/\s+/g, " ").trim();
}

const addressDiffers = computed((): boolean => {
	const report = normalizeAddress(props.reportAddress);
	const effective = normalizeAddress(effectiveRaw.value);
	if (report === "" || effective === "") return false;
	return report !== effective;
});

const badges = computed((): Badge[] => {
	const property = props.property;
	const result: Badge[] = [];
	if (!parcel.value) return result;
	if ((parcel.value.situs?.overridden?.length ?? 0) > 0) {
		result.push({
			label: "Corrected by district",
			title: "This district corrected the county assessment data",
			cls: "bg-info text-dark",
		});
	}
	if (addressDiffers.value) {
		result.push({
			label: "Differs from report address",
			title: `Report address: ${props.reportAddress}`,
			cls: "bg-warning text-dark",
		});
	}
	if (property?.match_quality === "nearest") {
		result.push({
			label: "Nearest parcel",
			title: "Matched the nearest parcel within 10 m",
			cls: "bg-secondary",
		});
	}
	if (property?.ambiguous) {
		result.push({
			label: "Multiple parcels",
			title: "More than one parcel covers this location",
			cls: "bg-secondary",
		});
	}
	return result;
});

function addRow(
	rows: Row[],
	label: string,
	value: string | number | null | undefined,
) {
	if (value == null) return;
	const text = String(value).trim();
	if (text === "") return;
	rows.push({ label, value: text });
}

const rows = computed((): Row[] => {
	const summary = parcel.value;
	if (!summary) return [];
	const situs = summary.situs ?? null;
	const result: Row[] = [];
	addRow(result, "Address", effectiveAddress.value);
	addRow(result, "APN", summary.external_id);
	addRow(result, "Owner", situs?.owner);
	addRow(
		result,
		"Property type",
		situs?.property_type ?? summary.property_type,
	);
	if (situs?.lot_size_acres != null) {
		addRow(
			result,
			"Lot size",
			`${situs.lot_size_acres.toLocaleString(undefined, {
				maximumFractionDigits: 2,
			})} ac`,
		);
	}
	addRow(result, "Year built", situs?.year_built);
	addRow(result, "Pools", situs?.pool_count);
	addRow(result, "Description", summary.description);
	addRow(result, "Region", summary.region);
	return result;
});

// Site labels pick up the loaded site's address when the store has it; the
// fetch below is best-effort and never blocks the card. The property usually
// arrives after mount, so watch the ids rather than fetching only on mount.
let siteFetchAttempted = false;
function maybeFetchSites(siteIDs: number[]) {
	if (siteFetchAttempted) return;
	if (siteIDs.length === 0) return;
	if (siteIDs.every((id) => storeSite.byID(id))) return;
	siteFetchAttempted = true;
	storeSite.fetchAll().catch(() => {
		// Addresses are a nicety; the link still works without them.
	});
}
watch(
	() => parcel.value?.site_ids ?? [],
	(siteIDs) => maybeFetchSites(siteIDs),
	{ immediate: true },
);

function siteLabel(siteID: number): string {
	const site = storeSite.byID(siteID);
	if (!site) return `Site #${siteID}`;
	const street = [site.address.number, site.address.street]
		.filter((part) => part && part.trim() !== "")
		.join(" ")
		.trim();
	const parts = [street, site.address.locality].filter(
		(part) => part && part.trim() !== "",
	);
	return parts.length > 0
		? `Site #${siteID} · ${parts.join(", ")}`
		: `Site #${siteID}`;
}

function zoomToProperty() {
	const geometry = parcel.value?.geometry_geojson;
	if (!geometry) return;
	const bounds = boundsFromGeoJSON(geometry, 20);
	if (bounds) emit("zoom", bounds);
}
</script>
